/**
 * The sun, as how far a shadow moves along the ground for each pixel of height (x across, y down).
 * It stands to the north-east and leans every shadow to the lower left, like the shading baked into the
 * tree and bush art.
 *
 * WORLD_SUN is the sun everything casts with by day -- walls, floors, roofs, trees, entity tiles and bodies
 * (building_shadow, foliage_shadow, lpc_composite_sprite) -- long enough that a merlon's shadow clears
 * its own foot and a tower's lies across the walk beside it; MOON by night. SUN is the short, compact cast bodies used
 * before; window.spriteShadowDirX / DirY bring a body-only sun back.
 *
 * Tune live: window.spriteShadowDirX / window.spriteShadowDirY (people), window.worldSunX /
 * window.worldSunY (the world); the world's shadows re-bake on the change.
 */
export const SUN = { x: -0.23, y: 0.13 }
export const WORLD_SUN = { x: -0.55, y: 0.31 }

/**
 * The moon: what the world casts its shadows with by night (building_shadow.ts shadowLight), faintly -- `strength`
 * is its shadows' share of the sun's -- and from the north-WEST, so they lean to the lower RIGHT where the sun's
 * lean left. It stays in the north like the sun: a shadow cast up the screen hides behind what casts it, and every
 * wall's south face is drawn as its shaded side. Its shadows grow from nothing as the sun's go (brightness 0.4) to
 * full strength at `full` (night, 0.3).
 * Tune live: window.moon = { x, y, strength, full }; window.moon = false for no shadows by night.
 */
export const MOON = { x: 0.45, y: 0.38, strength: 0.4, full: 0.3 }

export const worldMoon = (): typeof MOON | undefined => {
    const w = typeof window === "undefined" ? undefined : (window as any)
    if (w?.moon === false) return undefined
    return w?.moon && typeof w.moon === "object" ? { ...MOON, ...w.moon } : MOON
}

/**
 * A body's own shadow, matched to the world's: cast by the world sun, the world's deep cool shade colour,
 * drawn plainly (its sprites batched with the bodies: the world's multiply would cost each body a draw
 * call of its own) at `alpha`, times the room's shade strength, so it goes with the daylight.
 * A body stepping into a building's shade loses its shadow (the building already took the sun) and darkens
 * like the ground there, and gets both back stepping out: over `fadeMs`, not at once. A shadow falling into
 * shade (a wall's, a tree's) adds nothing there: it fades by the share of it that does. Its edge is as soft
 * as the world's baked shadows, `softness` px each way (sprites/soft_shadow.ts; 0: sharp).
 * Tune live: window.spriteShadowAlpha, window.spriteShadowFadeMs, window.spriteShadowSoftness;
 * window.spriteShadowDirX / DirY to give bodies a sun of their own again.
 */
export const BODY_SHADOW = { color: 0x1b2033, alpha: 0.5, fadeMs: 200, softness: 1.5,
    /** How tall a body stands, for where its shadow lies (sprite_shade.ts castShadePoints). */
    height: 48 }

/** `shown` moved toward `target` by `dtMs` of a `fadeMs` fade across the whole 0..1: linearly, landing exactly. */
export const easeToward = (shown: number, target: number, dtMs: number, fadeMs: number): number => {
    if (fadeMs <= 0) return target
    const step = dtMs / fadeMs
    return shown < target ? Math.min(target, shown + step) : Math.max(target, shown - step)
}

export const sunDirection = (): { x: number; y: number } => {
    const w = typeof window === "undefined" ? undefined : (window as any)
    return {
        x: typeof w?.spriteShadowDirX === "number" ? w.spriteShadowDirX : SUN.x,
        y: typeof w?.spriteShadowDirY === "number" ? w.spriteShadowDirY : SUN.y,
    }
}

export const worldSunDirection = (): { x: number; y: number } => {
    const w = typeof window === "undefined" ? undefined : (window as any)
    return {
        x: typeof w?.worldSunX === "number" ? w.worldSunX : WORLD_SUN.x,
        y: typeof w?.worldSunY === "number" ? w.worldSunY : WORLD_SUN.y,
    }
}
